import { Chain } from "@rainbow-me/rainbowkit";
import { RouteType } from "store/global/getQuote";
import { Address, getAddress, isAddress } from "viem";
import { DUMMY_WALLET } from "../config/consts";
import { API_BASE_URL, JAM_VERSION } from "../config/env";
import {
  MakerToken,
  Receiver,
  SelectedToken,
  TakerToken,
  WithAllocationPct,
} from "../store/global/GlobalStore";
import { Token } from "../store/global/TokenEnum";
import { TokenList } from "../store/global/getAllTokenLists";
import { Amount } from "../types/amount";
import { Quote } from "../types/quote";
import { NETWORK_TO_PRICE_ENDPOINT_MAP } from "./bootstrapWagmi";
import { getWrappedSymbolForChain } from "./chainUtils";
import { OrderType } from "./orderTypeUtils";
import { getTokenAddressBySymbol } from "./orderUtils";
import { PermitType } from "./permitUtils";
import { isNativeToken } from "./wrapUtils";
import qs from "qs";

//Approval Type is what the Quote endpoint specifies as possible parameters
export type ApprovalType = "Permit" | "Permit2" | "Standard" | undefined;
interface GetQuoteUrlParams {
  orderType: OrderType | undefined;
  takerTokens: Array<TakerToken>;
  makerTokens: Array<MakerToken>;
  network: Chain;
  address: Address;
  tokenList: TokenList | undefined;
  receiver: Receiver;
  permitType: PermitType | undefined;
  forceSolver?: string | undefined;
}

interface QuoteRequestParams {
  approval_type: string;
  buy_tokens: string;
  sell_tokens: string;
  taker_address: string;
  receiver_address: string;
  sell_amounts: string;
  buy_amounts: string;
  sell_tokens_ratios: string;
  buy_tokens_ratios: string;
  source: string;
  include_solvers: string;
}

export const getQuoteUrl = ({
  orderType,
  takerTokens,
  makerTokens,
  network,
  address,
  tokenList,
  receiver,
  permitType,
  forceSolver,
}: GetQuoteUrlParams): string | undefined => {
  if (!orderType) {
    return undefined;
  }

  // We send a dummy wallet when the user hasn't connected a wallet.
  const addressToUse = address.length === 0 ? DUMMY_WALLET : address;
  const node = NETWORK_TO_PRICE_ENDPOINT_MAP[network.name];
  const ENDPOINT_BASE = `${API_BASE_URL}/router/${node}/${JAM_VERSION}/quote`;
  const query: Partial<QuoteRequestParams> = {};

  const approvalType: ApprovalType = permitType === "permit1" ? "Permit" : "Permit2";

  // We replace the contract address for native token on taker side to the wrapped tokens contract address. This is due to Native token to ERC20 not being supported on taker side by /quote endpoint.
  const serializedTakerTokens = takerTokens
    .map(({ token }) => {
      const tokenSymbol = isNativeToken(token, network) ? getWrappedSymbolForChain(network) : token;
      return getTokenAddressBySymbol(tokenList, network, tokenSymbol);
    })
    .join(",");
  const serializedMakerTokens = makerTokens
    .map(({ token }) => getTokenAddressBySymbol(tokenList, network, token))
    .join(",");
  const receiverAddress = receiver.address;

  query.buy_tokens = serializedMakerTokens;
  query.sell_tokens = serializedTakerTokens;
  query.taker_address = addressToUse;
  query.receiver_address =
    isAddress(receiverAddress) && getAddress(receiverAddress) === receiverAddress
      ? receiver.address
      : address;

  query.source = "bebop.xyz";
  query.approval_type = approvalType;
  if (forceSolver) {
    query.include_solvers = forceSolver;
  }

  if (orderType === "121" || orderType === "M21") {
    const takerAmountsArray = takerTokens
      .map(({ amount, token }) => {
        const tokenDecimals = tokenList?.[token]?.chainInfo.decimals ?? 18;
        const tokenDecimalsAmount = new Amount((10 ** tokenDecimals).toString());
        return amount.mul(tokenDecimalsAmount).value.toFixed();
      })
      .join(",");
    query.sell_amounts = takerAmountsArray;
  }
  if (orderType === "12M") {
    const tokenDecimals = tokenList?.[takerTokens[0].token]?.chainInfo.decimals ?? 18;
    const tokenDecimalsAmount = new Amount((10 ** tokenDecimals).toString());
    const takerAmounts = takerTokens[0].amount.mul(tokenDecimalsAmount).value.toFixed();
    const serializedMakerPercents = makerTokens.map(({ pct }) => pct / 100).join(",");
    query.sell_amounts = takerAmounts;
    query.buy_tokens_ratios = serializedMakerPercents;
  }

  const queryString = qs.stringify(query);

  return `${ENDPOINT_BASE}?${queryString}`;
};
export interface TokenSummary {
  amount: Amount;
  fiatValue: Amount;
}

export const getTokenIn = (quote: Quote, token: Token): TokenSummary => {
  if (isOneToOne(quote) || isOneToMany(quote)) {
    return {
      amount: Object.values(quote.takerTokens)[0].amount,
      fiatValue: Object.values(quote.takerTokens)[0].amountUsd,
    };
  } else if (isManyToOne(quote)) {
    return {
      amount: quote.takerTokens[token]?.amount ?? Amount.ZERO,
      fiatValue: quote.takerTokens[token]?.amountUsd ?? Amount.ZERO,
    };
  }

  return {
    amount: Amount.ZERO,
    fiatValue: Amount.ZERO,
  };
};

export const getTokenOutSummary = (quote: Quote, token: Token): TokenSummary => {
  if (isOneToOne(quote)) {
    return {
      amount: quote.makerTokens[token]?.amount ?? Amount.ZERO,
      fiatValue: quote.makerTokens[token]?.amountUsd ?? Amount.ZERO,
    };
  } else if (isOneToMany(quote)) {
    return {
      amount: quote.makerTokens[token]?.amount ?? Amount.ZERO,
      fiatValue: quote.makerTokens[token]?.amountUsd ?? Amount.ZERO,
    };
  } else if (isManyToOne(quote)) {
    return {
      amount: quote.makerTokens[token]?.amount ?? Amount.ZERO,
      fiatValue: quote.makerTokens[token]?.amountUsd ?? Amount.ZERO,
    };
  }

  return {
    amount: Amount.ZERO,
    fiatValue: Amount.ZERO,
  };
};
export const getTokenOut = (quote: Quote): Token | null => {
  if (isOneToOne(quote)) {
    return Object.keys(quote.takerTokens).map(entry => entry as Token)[0];
  } else if (isManyToOne(quote)) {
    return Object.keys(quote.makerTokens).map(entry => entry as Token)[0];
  } else if (isOneToMany(quote)) {
    return Object.keys(quote.takerTokens).map(entry => entry as Token)[0];
  }
  return null;
};

export const getGasCost = (quote?: Quote): Amount => {
  return quote?.usdGasFee ?? Amount.ZERO;
};

export const calculateFee = (quote: Quote): Amount => {
  if (isOneToOne(quote) || isOneToMany(quote)) {
    const takerToken = Object.values(quote.takerTokens)[0];
    return quote.usdGasFee.div(takerToken.priceUsd);
  } else {
    const makerToken = Object.values(quote.makerTokens)[0];
    if (!makerToken.amountBeforeFee) {
      return Amount.ZERO;
    }
    return makerToken.amountBeforeFee.sub(makerToken.amount);
  }
};

// Quote Type narrower
export const isOneToOne = (quote: Quote): boolean => quote.type === "121";

export const isOneToMany = (quote: Quote): boolean =>
  quote.type === "12MPercentages" || quote.type === "12M";

export const isManyToOne = (quote: Quote): boolean => quote.type === "M21";

export const getSelectedTokenSymbolArray = (
  takerOrMakerTokens: Array<SelectedToken> | Array<SelectedToken & WithAllocationPct>
) => {
  return takerOrMakerTokens.map(e => e.token);
};

export const isBestQuotePmm = (routeType: RouteType) => {
  return routeType === "PMM";
};
export const isBestQuoteJam = (routeType: RouteType) => {
  return routeType === "Jam";
};
